/**
 * Ontology 탭 상단 Hero — 팩 칩 + 통계 + 주요 액션 바.
 *
 * 디자인:
 * - 좌측: 슬라이스 라벨 (packIds 가 있으면 팩 칩 리스트, 없으면 Custom) · kinds/domains/relations/shapes 카운트 ("N used" 부제)
 * - 우측: "Unsaved changes" 배지, 액션 (Edit packs / Import / Export / Save / Discard)
 * - Save/Discard 는 dirty 상태에서만 활성화 (Discard 는 ghost, Save 는 primary)
 *
 * 규격화된 그래프 RAG 원칙: 슬라이스 밖 데이터는 항상 400. enforcement 토글은 더 이상 없다.
 */
import type { OntologySlice, OntologyUsage } from "../../api/admin-client.ts"

interface OntologyHeroProps {
	readonly draft: OntologySlice
	readonly isDirty: boolean
	readonly usage: OntologyUsage | undefined
	readonly warningsCount?: number
	readonly saving?: boolean
	readonly onEditPacks: () => void
	/**
	 * MasterComposer 모달 열기 — 마스터 블럭 전부(현재 111, BLOCKS_TUPLE.length SSOT)에서 시작해 제외 방식으로 slice 를 구성한다.
	 * PackPicker 와 병렬 경로.
	 */
	readonly onEditMaster: () => void
	readonly onImport: () => void
	readonly onExport: () => void
	readonly onSave: () => void
	readonly onDiscard: () => void
}

/**
 * draft 에 정의된 kind 중 실제로 usage 에 등장한 개수.
 * @param names draft 의 이름 배열
 * @param usage `/admin/services/:slug/ontology-slice/usage` 응답
 */
function countUsed(names: ReadonlyArray<string>, usage: Record<string, number> | undefined): number {
	if (!usage) return 0
	let count = 0
	for (const name of names) {
		if ((usage[name] ?? 0) > 0) count++
	}
	return count
}

export function OntologyHero(props: OntologyHeroProps) {
	const { draft, isDirty, usage, warningsCount, saving } = props

	const kindsUsed = countUsed(
		draft.kinds.map((k) => k.name),
		usage?.kinds
	)
	const domainsUsed = countUsed(
		draft.domains.map((d) => d.name),
		usage?.domains
	)
	const relationsUsed = countUsed(
		draft.relations.map((r) => r.name),
		usage?.relations
	)
	const packIds = draft.packIds ?? []

	return (
		<div className="ontology-hero">
			<div className="ontology-hero-main">
				<div className="ontology-hero-title">
					{packIds.length > 0 ? (
						<div className="ontology-hero-packs">
							{packIds.map((id) => (
								<span key={id} className="chip ontology-hero-pack-chip">
									{id}
								</span>
							))}
						</div>
					) : (
						<span className="ontology-hero-preset">커스텀 슬라이스</span>
					)}
					{isDirty && (
						<span className="unsaved-badge" title="저장되지 않은 변경사항">
							<span className="unsaved-dot" />
							저장되지 않은 변경사항
						</span>
					)}
					{typeof warningsCount === "number" && warningsCount > 0 && (
						<span
							className="badge badge-warning"
							title="슬라이스 밖이어서 knowledge_upsert 에서 거부될 항목 수"
						>
							{warningsCount}개 슬라이스 외부
						</span>
					)}
				</div>
				<div className="ontology-hero-stats">
					<HeroStat label="종류" total={draft.kinds.length} used={kindsUsed} />
					<HeroStat label="도메인" total={draft.domains.length} used={domainsUsed} />
					<HeroStat label="관계" total={draft.relations.length} used={relationsUsed} />
					<HeroStat label="형태" total={draft.shapes.length} />
				</div>
			</div>

			<div className="ontology-hero-actions">
				<div className="ontology-hero-buttons">
					<button
						type="button"
						className="btn btn-ghost btn-sm"
						onClick={props.onEditPacks}
						disabled={saving}
					>
						팩 편집
					</button>
					<button
						type="button"
						className="btn btn-ghost btn-sm"
						onClick={props.onEditMaster}
						disabled={saving}
						title="마스터 블럭 전부에서 시작해 불필요한 블럭/연결을 제외해 slice 를 구성합니다"
					>
						마스터 컴포저
					</button>
					<button type="button" className="btn btn-ghost btn-sm" onClick={props.onImport} disabled={saving}>
						가져오기
					</button>
					<button type="button" className="btn btn-ghost btn-sm" onClick={props.onExport} disabled={saving}>
						내보내기
					</button>
					{isDirty && (
						<>
							<button
								type="button"
								className="btn btn-ghost btn-sm"
								onClick={props.onDiscard}
								disabled={saving}
							>
								되돌리기
							</button>
							<button
								type="button"
								className="btn btn-primary btn-sm"
								onClick={props.onSave}
								disabled={saving}
							>
								{saving ? "저장 중…" : "슬라이스 저장"}
							</button>
						</>
					)}
				</div>
			</div>
		</div>
	)
}

function HeroStat(props: { readonly label: string; readonly total: number; readonly used?: number }) {
	return (
		<div className="ontology-hero-stat">
			<span className="ontology-hero-stat-total">{props.total}</span>
			<span className="ontology-hero-stat-label">{props.label}</span>
			{typeof props.used === "number" && (
				<span className="ontology-hero-stat-used">{props.used}개 사용 중</span>
			)}
		</div>
	)
}
